/** Admin one-off: preview then apply Title Case to names/companies/addresses already stored. */
import React, { useContext, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const LABEL = { users: 'Accounts (installers & staff)', contacts: 'Contacts', training_registrations: 'Training bookings', training_enquiries: 'Training enquiries', access_requests: 'Access requests' };

export default function TidyNamesButton({ onDone }) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  if (user?.role !== 'admin') return null;

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/tidy-names?dry_run=${dryRun}`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Failed');
      if (dryRun) { setPreview(d); setOpen(true); } else { toast.success(`Tidied ${d.total_changes} record${d.total_changes === 1 ? '' : 's'}`); setOpen(false); setPreview(null); onDone?.(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => run(true)} disabled={busy} title="Title-case names, companies and addresses already in the system" data-testid="tidy-names-btn">
        {busy && !open ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Tidy names</span>
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="sm:max-w-lg" data-testid="tidy-names-dialog">
          <DialogHeader><DialogTitle>Tidy existing names</DialogTitle><DialogDescription>Applies Title Case (and upper-case postcodes) to records already stored. Emails, product names and free text are untouched.</DialogDescription></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <ul className="grid grid-cols-2 gap-1.5 text-xs" data-testid="tidy-names-counts">
                {Object.entries(preview.collections).map(([k, n]) => <li key={k} className="flex justify-between rounded-md bg-gray-50 px-2 py-1"><span>{LABEL[k] || k}</span><strong>{n}</strong></li>)}
              </ul>
              <div className="font-semibold" data-testid="tidy-names-total">{preview.total_changes} record{preview.total_changes === 1 ? '' : 's'} would change</div>
              {preview.samples.length > 0 && (
                <ul className="max-h-48 overflow-auto divide-y text-xs border rounded-md">
                  {preview.samples.map((s, i) => <li key={i} className="px-2 py-1.5"><span className="text-gray-400 mr-1">{LABEL[s.collection] || s.collection}:</span>{Object.keys(s.after).map((k) => <span key={k} className="mr-2"><s className="text-gray-400">{s.before[k]}</s> → <strong>{s.after[k]}</strong></span>)}</li>)}
                </ul>
              )}
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)} data-testid="tidy-names-cancel">Cancel</Button><Button onClick={() => run(false)} disabled={busy || preview.total_changes === 0} className="bg-gray-900 text-white" data-testid="tidy-names-apply">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Apply to {preview.total_changes}</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
